import { CSSProperties } from 'react';
import { Avatar } from '../core/Avatar';
import { Button } from '../core/Button';

/**
 * HostSwipeCard — full-bleed mobile host card for a swipeable carousel
 * (Choose Host screen). Profile photo fills the card, a bottom scrim carries
 * Alma's note and a "Know more" CTA — but only before this host is chosen;
 * once `state` is 'chosen', the card collapses to just name, headline, and
 * the badge, since the ConfirmBar below it takes over from there. See
 * HostChoiceCard for the desktop side-by-side equivalent.
 *
 * state: 'default' | 'chosen' (gold ring + badge, note/CTA hidden)
 *        | 'dimmed' (28% opacity, pointer-events off — the other card).
 */
export interface HostSwipeCardProps {
  name: string;
  avatarUrl?: string | null;
  headline?: string | null;
  /** Alma's short note (quote marks auto-added). Hidden once `state` is 'chosen'. */
  almaNote?: string;
  state?: 'default' | 'chosen' | 'dimmed';
  onKnowMore?: () => void;
  style?: CSSProperties;
}

export function HostSwipeCard({
  name, avatarUrl, headline, almaNote,
  state = 'default', onKnowMore, style,
}: HostSwipeCardProps) {
  const chosen = state === 'chosen';
  const dimmed = state === 'dimmed';

  return (
    <div
      style={{
        position: 'relative', width: '100%', height: '100%',
        borderRadius: 20, overflow: 'hidden', flexShrink: 0,
        background: 'var(--surface)',
        transition: 'opacity .3s',
        ...(dimmed ? { opacity: 0.28, pointerEvents: 'none' as const } : null),
        ...style,
      }}
    >
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={name}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Avatar name={name} size={96} />
        </div>
      )}

      <div
        style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,.95) 0%, rgba(0,0,0,.72) 36%, rgba(0,0,0,.14) 62%, transparent 76%)',
        }}
      />

      {chosen ? (
        <span
          style={{
            position: 'absolute', top: 16, right: 16,
            display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 999, padding: '5px 11px', fontSize: 11,
            background: 'rgb(var(--gold))', color: 'var(--on-gold)', fontFamily: 'var(--font-sans)', fontWeight: 600,
          }}
        >
          Chosen
        </span>
      ) : null}

      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '20px 20px 22px', fontFamily: 'var(--font-sans)' }}>
        <div style={{ fontFamily: 'var(--font-serif)', fontSize: 24, color: 'rgb(var(--cream))', lineHeight: 1.15 }}>{name}</div>
        {headline ? (
          <div style={{ fontSize: 12.5, color: 'rgb(var(--cream) / .7)', marginTop: 4, marginBottom: !chosen && almaNote ? 14 : 0, lineHeight: 1.4, textWrap: 'pretty' }}>
            {headline}
          </div>
        ) : null}

        {!chosen && almaNote ? (
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
              <i className="ti ti-sparkles" style={{ color: 'rgb(var(--gold))', fontSize: 11 }} />
              <span style={{ color: 'rgb(var(--gold))', fontSize: 10.5, fontWeight: 500 }}>Alma's take</span>
            </div>
            <div
              style={{
                fontFamily: 'var(--font-serif)', fontSize: 13, lineHeight: 1.4, color: 'rgb(var(--cream) / .92)',
                textWrap: 'pretty',
              }}
            >
              &ldquo;{almaNote}&rdquo;
            </div>
          </div>
        ) : null}

        {!chosen ? (
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            onClick={onKnowMore}
            style={{
              border: '1px solid rgb(var(--cream) / .4)',
              background: 'rgb(var(--cream) / .08)', color: 'rgb(var(--cream))',
              backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
            }}
          >
            Know more
          </Button>
        ) : null}
      </div>

      {chosen ? (
        // A dedicated top-layer overlay, not a `box-shadow` on the card
        // itself — the full-bleed photo below is `position: absolute` and
        // paints ABOVE the card's own background/box-shadow in stacking
        // order (descendants stack over an ancestor's own decoration), so
        // an inset shadow on the root div was being fully covered by the
        // photo, invisible regardless of the clipping fix. Being the last
        // child here puts it on top of everything, inset so it still can't
        // bleed past the card into the scroller's clipped overflow.
        <div
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, borderRadius: 20, boxShadow: 'inset 0 0 0 2px rgb(var(--gold))', pointerEvents: 'none' }}
        />
      ) : null}
    </div>
  );
}

export default HostSwipeCard;
